import React, { useContext, useEffect } from 'react'
import { ListGroup } from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'

import NavItem from '../NavItem'
import LoopNavCollapse from './index'
import NavIcon from '../NavIcon'
import NavBadge from '../NavBadge'
import { useTranslation } from 'react-i18next'

import { ConfigContext } from '../../../../../contexts/ConfigContext'
import * as actionType from '../../../../../store/actions'
import objectPath from 'object-path'
import { ROUTE_HIS } from 'designer/component/action/Config'

const normalizeMenuChildren = (items = []) => {
  if (Array.isArray(items)) return items
  if (items && typeof items === 'object') return Object.values(items)

  return []
}

const getFirstSelectableMenuItem = (items = []) => {
  for (const item of normalizeMenuChildren(items)) {
    if (!item) continue

    if (
      item.type === 'item' &&
      item.url &&
      item.linkType !== 'url' &&
      objectPath.get(item, 'menu.data.openNewTab') !== 1
    ) {
      return item
    }

    if (item.children) {
      const child = getFirstSelectableMenuItem(item.children)
      if (child) return child
    }
  }

  return null
}

const resolveMenuUrl = (item = {}) => {
  const url = item.url || ''

  if (objectPath.get(item, 'menu.data.openNewTab') === 1) {
    if (item.linkType === 'router') {
      return url.startsWith('/') ? `${ROUTE_HIS}${url}` : `${ROUTE_HIS}/${url}`
    }
  }

  return url
}

const getCurrentRoutePath = () =>
  document.location.hash
    ? document.location.hash.replace(/^#/, '')
    : document.location.pathname

const NavCollapse = ({ collapse, type }) => {
  const history = useHistory()
  const configContext = useContext(ConfigContext)
  const { t } = useTranslation()
  const { dispatch } = configContext

  const { layout, layoutType, isOpen, isTrigger, menuClick } = configContext.state
  const isDarkMenu = layout !== 'horizontal' && ['menu-dark', 'dark'].includes(layoutType)
  const menuStyle = isDarkMenu ? { color: 'inherit' } : {}
  const currentCollapse = localStorage.getItem('currentCollapse')

  useEffect(() => {
    const currentIndex = document.location.pathname
      .toString()
      .split('/')
      .findIndex((id) => id === collapse.id)

    if (currentIndex > -1) {
      console.log('currentCollapse', [
        collapse.id,
        currentCollapse,
        currentIndex,
      ])

      dispatch({
        type: actionType.COLLAPSE_TOGGLE,
        menu: { id: collapse.id, type: type },
      })
    }
  }, [collapse, dispatch, type])

  let navItems = ''
  if (collapse.children) {
    const collapses = collapse.children
    navItems = Object.keys(collapses).map((item) => {
      item = collapses[item]
      switch (item.type) {
        case 'collapse':
          return <LoopNavCollapse key={item.id} collapse={item} type="sub" />
        case 'item':
          return <NavItem layout={layout} key={item.id} item={item} />
        default:
          return false
      }
    })
  }

  const firstSelectableItem = getFirstSelectableMenuItem(collapse.children)
  const isCurrentMenuInCollapse = (items = []) =>
    normalizeMenuChildren(items).some((item) => {
      if (!item) return false
      if (
        item.type === 'item' &&
        (item.id === menuClick?.id || item.url === getCurrentRoutePath())
      ) {
        return true
      }

      return item.children ? isCurrentMenuInCollapse(item.children) : false
    })

  let itemTitle = t(collapse.title)
  if (collapse.icon) {
    itemTitle = <span className="pcoded-mtext" style={menuStyle}>{t(collapse.title)}</span>
  }

  let navLinkClass = ['nav-link']

  let navItemClass = ['nav-item', 'pcoded-hasmenu']
  const openIndex = isOpen.findIndex(
    (id) => id === collapse.id && currentCollapse === id,
  )
  if (openIndex > -1) {
    navItemClass = [...navItemClass, 'active']
    if (layout !== 'horizontal') {
      navLinkClass = [...navLinkClass, 'active']
    }
  }

  const triggerIndex = isTrigger.findIndex(
    (id) => id === collapse.id && currentCollapse === id,
  )
  if (triggerIndex > -1) {
    navItemClass = [...navItemClass, 'pcoded-trigger']
  }

  const currentIndex = document.location.pathname
    .toString()
    .split('/')
    .findIndex((id) => id === collapse.id)
  if (currentIndex > -1) {
    navItemClass = [...navItemClass, 'active']
    if (layout !== 'horizontal') {
      navLinkClass = [...navLinkClass, 'active']
    }
  }

  if (isDarkMenu && navLinkClass.includes('active')) {
    navLinkClass = [...navLinkClass, 'text-white']
  }

  const subContent = (
    <React.Fragment>
      <Link
        to="#"
        className={navLinkClass.join(' ') + ' d-flex align-items-center'}
        onClick={(event) => {
          event.preventDefault()

          const willOpen =
            type === 'sub'
              ? isTrigger.indexOf(collapse.id) === -1
              : triggerIndex === -1

          dispatch({
            type: actionType.COLLAPSE_TOGGLE,
            menu: { id: collapse.id, type: type },
          })
          dispatch({ type: actionType.COLLAPSE_CLICK, collapseClick: collapse })

          if (
            willOpen &&
            firstSelectableItem &&
            !isCurrentMenuInCollapse(collapse.children)
          ) {
            dispatch({
              type: actionType.MENU_CLICK,
              menuClick: firstSelectableItem,
            })
            history.push(resolveMenuUrl(firstSelectableItem))
          }
        }}
      >
        <NavIcon items={collapse} />
        {itemTitle}
        <NavBadge items={collapse} />
      </Link>
      <ListGroup
        variant="flush"
        bsPrefix=" "
        as="ul"
        className="pcoded-submenu"
      >
        {navItems}
      </ListGroup>
    </React.Fragment>
  )

  let mainContent = ''
  if (layout === 'horizontal') {
    mainContent = (
      <ListGroup.Item
        as="li"
        bsPrefix=" "
        className={navItemClass.join(' ')}
        onMouseLeave={() =>
          dispatch({
            type: actionType.NAV_COLLAPSE_LEAVE,
            menu: { id: collapse.id, type: type },
          })
        }
        onMouseEnter={() =>
          dispatch({
            type: actionType.COLLAPSE_TOGGLE,
            menu: { id: collapse.id, type: type },
          })
        }
      >
        {subContent}
      </ListGroup.Item>
    )
  } else {
    mainContent = (
      <ListGroup.Item as="li" bsPrefix=" " className={navItemClass.join(' ')}>
        {subContent}
      </ListGroup.Item>
    )
  }

  return <React.Fragment>{mainContent}</React.Fragment>
}

export default NavCollapse
